/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import gql from 'graphql-tag';

import Imgix from 'js/components/Imgix';
import imgix from 'js/lib/imgix';

import { Grid, Typography, Typography2 } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import type { PartnerPageQuery_PartnersV1Resource_shortName_partners as Partner } from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';

const styles = {
  partnerBanner: (backgroundImgSrc: string) => css`
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    background-image: url(${backgroundImgSrc});
    height: 320px;
    width: 100%;
  `,
  partnerName: css`
    margin-top: 20px;
  `,
  partnerInfoContainer: css`
    max-width: 950px;
  `,
  partnerLogo: css`
    margin-top: -60px;
    width: 188px;
    height: 188px;
    padding: 4px;
    box-shadow: inset 0 0 3px 0 var(--cds-color-grey-500);
    background-color: var(--cds-color-neutral-background-primary);
  `,
  partnerDescription: css`
    margin-left: -30px;

    @media (max-width: 995px) {
      margin-left: 10px;
    }
  `,
};

type Props = {
  partner: Partner | null;
};

export const PartnerHeader = ({ partner }: Props) => {
  if (!partner) {
    return null;
  }

  const backgroundImage = imgix.processImage(partner.landingPageBanner, { width: 1200, dpr: 1 });

  return (
    <div>
      <div css={styles.partnerBanner(backgroundImage)} />
      <Grid container css={styles.partnerInfoContainer} className="bt3-container">
        {partner.squareLogo && (
          <Grid item xs={3}>
            <div css={styles.partnerLogo}>
              <Imgix src={partner.squareLogo} alt={`${partner.name} Logo`} maxWidth={180} maxHeight={180} />
            </div>
          </Grid>
        )}
        <Grid item css={styles.partnerDescription} xs={9}>
          <Typography component="h1" variant="h1semibold" css={styles.partnerName} data-e2e="partner-header">
            {partner.name}
          </Typography>
          <Typography2 component="p" variant="bodySecondary">
            {partner.description}
          </Typography2>
        </Grid>
      </Grid>
    </div>
  );
};

const partnerHeaderFragment = gql`
  fragment PartnerHeaderFragment on PartnersV1 {
    landingPageBanner
    squareLogo
    name
    description
  }
`;

export default withFragments({ partnerHeaderFragment })(PartnerHeader);
